/**
 * 🏪 AffiliateLokalePage - Meine Lokale (konvertierte Restaurants)
 */

import React from 'react';
import { AffiliateLayout } from '@/components/affiliate/layout/AffiliateLayout';
import { StatusBadge } from '@/components/affiliate/badges/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { 
  Store, 
  TrendingUp, 
  Calendar,
  ExternalLink,
  MoreHorizontal,
  Plus,
  CheckCircle2,
  Clock,
  AlertCircle,
  Wallet
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ============================================
// TYPES & MOCK DATA
// ============================================

type PackageType = 'A' | 'B' | 'C';
type RestaurantStatus = 'aktiv' | 'inaktiv' | 'pending' | 'verlaengert';

interface Restaurant {
  id: number;
  name: string;
  address: string;
  city: string;
  status: RestaurantStatus;
  package: PackageType;
  monthlyRevenue: number;
  commission: number;
  joinedDate: string;
  lastOrder: string;
}

const MOCK_RESTAURANTS: Restaurant[] = [
  {
    id: 1,
    name: 'City Grill Berlin',
    address: 'Hauptstraße 123',
    city: 'Berlin',
    status: 'aktiv',
    package: 'A',
    monthlyRevenue: 4580,
    commission: 45.80,
    joinedDate: '2024-01-15',
    lastOrder: 'heute',
  },
  {
    id: 2,
    name: 'Food Lounge',
    address: 'Marktplatz 7',
    city: 'Hamburg',
    status: 'verlaengert',
    package: 'B',
    monthlyRevenue: 6230,
    commission: 62.30,
    joinedDate: '2024-02-20',
    lastOrder: 'gestern',
  },
  {
    id: 3,
    name: 'Pizza World',
    address: 'Hafenstr. 45',
    city: 'München',
    status: 'pending',
    package: 'C',
    monthlyRevenue: 0,
    commission: 0,
    joinedDate: '2024-03-28',
    lastOrder: '-',
  },
  {
    id: 4,
    name: 'Burger House',
    address: 'Ringstraße 12',
    city: 'Köln',
    status: 'aktiv',
    package: 'A',
    monthlyRevenue: 3420,
    commission: 34.20,
    joinedDate: '2024-01-10',
    lastOrder: 'heute',
  },
];

const PACKAGE_CONFIG: Record<PackageType, { label: string; color: string; bgColor: string }> = {
  A: { label: 'Paket A', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  B: { label: 'Paket B', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  C: { label: 'Paket C', color: 'text-purple-700', bgColor: 'bg-purple-100' },
};

const STATUS_CONFIG: Record<RestaurantStatus, { label: string; variant: 'success' | 'error' | 'warning' | 'info'; icon: typeof CheckCircle2 }> = {
  aktiv: { label: 'Aktiv', variant: 'success', icon: CheckCircle2 },
  verlaengert: { label: 'Verlängert', variant: 'info', icon: CheckCircle2 },
  pending: { label: 'Ausstehend', variant: 'warning', icon: Clock },
  inaktiv: { label: 'Inaktiv', variant: 'error', icon: AlertCircle },
};

// ============================================
// COMPONENTS
// ============================================

const RestaurantCard: React.FC<{ restaurant: Restaurant }> = ({ restaurant }) => {
  const packageConfig = PACKAGE_CONFIG[restaurant.package];
  const statusConfig = STATUS_CONFIG[restaurant.status];

  return (
    <Card className="bg-white/80 backdrop-blur-xl border-white/60 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.1)] transition-colors overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Package Badge mit Icon */}
            <div className={cn(
              'w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg',
              packageConfig.bgColor, packageConfig.color
            )}>
              {restaurant.package}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{restaurant.name}</h3>
              <p className="text-sm text-gray-500">{restaurant.city}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge variant={statusConfig.variant} label={statusConfig.label} size="sm" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 py-4 border-t border-b border-gray-100">
          <div>
            <p className="text-xs text-gray-500 mb-1">Monatsumsatz</p>
            <p className="font-semibold">€{restaurant.monthlyRevenue.toLocaleString('de-DE')}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Provision</p>
            <p className="font-semibold text-emerald-600">€{restaurant.commission.toLocaleString('de-DE')}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Letzte Bestellung</p>
            <p className="font-semibold">{restaurant.lastOrder}</p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Calendar className="w-3 h-3" />
            <span>Dabei seit {new Date(restaurant.joinedDate).toLocaleDateString('de-DE')}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-9">
              <ExternalLink className="w-4 h-4 mr-1" />
              Profil
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>Details anzeigen</DropdownMenuItem>
                <DropdownMenuItem>Statistiken</DropdownMenuItem>
                <DropdownMenuItem>Kontakt aufnehmen</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// MAIN PAGE
// ============================================

export const AffiliateLokalePage: React.FC = () => {
  const totalRevenue = MOCK_RESTAURANTS.reduce((sum, r) => sum + r.monthlyRevenue, 0);
  const totalCommission = MOCK_RESTAURANTS.reduce((sum, r) => sum + r.commission, 0);
  const activeCount = MOCK_RESTAURANTS.filter(r => r.status === 'aktiv' || r.status === 'verlaengert').length;

  return (
    <AffiliateLayout
      pageTitle="Meine Lokale"
      pageSubtitle="Übersicht deiner geworbenen Restaurants"
      headerActions={
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25">
          <Plus className="w-4 h-4 mr-2" />
          Lokal hinzufügen
        </Button>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-white/80 backdrop-blur-xl border-white/60 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 rounded-xl">
              <Store className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Aktive Lokale</p>
              <p className="text-2xl font-bold">{activeCount}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white/80 backdrop-blur-xl border-white/60 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Gesamtumsatz</p>
              <p className="text-2xl font-bold">€{totalRevenue.toLocaleString('de-DE')}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white/80 backdrop-blur-xl border-white/60 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-100 rounded-xl">
              <Wallet className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Provision (Monat)</p>
              <p className="text-2xl font-bold text-emerald-600">€{totalCommission.toLocaleString('de-DE')}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white/80 backdrop-blur-xl border-white/60 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ausstehend</p>
              <p className="text-2xl font-bold">1</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Package Übersicht */}
      <Card className="mb-6 bg-gradient-to-r from-emerald-50/80 to-blue-50/80 border-emerald-100 rounded-xl">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4 sm:gap-8">
            <div className="flex items-center gap-2">
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm', PACKAGE_CONFIG.A.bgColor, PACKAGE_CONFIG.A.color)}>A</div>
              <span className="text-sm"><strong>{MOCK_RESTAURANTS.filter(r => r.package === 'A').length}</strong> Lokale mit Paket A</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm', PACKAGE_CONFIG.B.bgColor, PACKAGE_CONFIG.B.color)}>B</div>
              <span className="text-sm"><strong>{MOCK_RESTAURANTS.filter(r => r.package === 'B').length}</strong> Lokale mit Paket B</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm', PACKAGE_CONFIG.C.bgColor, PACKAGE_CONFIG.C.color)}>C</div>
              <span className="text-sm"><strong>{MOCK_RESTAURANTS.filter(r => r.package === 'C').length}</strong> Lokale mit Paket C</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Restaurant Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {MOCK_RESTAURANTS.map(restaurant => (
          <RestaurantCard key={restaurant.id} restaurant={restaurant} />
        ))}
      </div>

      {MOCK_RESTAURANTS.length === 0 && (
        <Card className="p-12 text-center bg-white/80 backdrop-blur-xl border-white/60 rounded-2xl">
          <Store className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Noch keine Lokale</h3>
          <p className="text-gray-500 mb-4">Starte jetzt mit der Akquise und verdiene Provisionen!</p>
          <Button className="bg-emerald-600 hover:bg-emerald-700">
            Ersten Lead erfassen
          </Button>
        </Card>
      )}
    </AffiliateLayout>
  );
};

export default AffiliateLokalePage;
